/**
 * Common API Schemas
 *
 * Shared schemas used across all API domains.
 */

import { z } from 'zod/v4';

/** The channel filter of the unified dashboard and its sibling reports: `records.type`. */
export const DASHBOARD_FLOW_CHANNELS = ['call', 'chat'] as const;
export const dashboardFlowChannelSchema = z.enum(DASHBOARD_FLOW_CHANNELS);

/**
 * A read of a feature above the organization's tier answered with its demo
 * data only (#2816): the tier gate said `tier_not_eligible` and the
 * organization is in its demo window (demo records and no real record yet),
 * so the route read demo rows and nothing else instead of answering 403.
 * Absent on every ordinary answer.
 */
export const lockedDemoOnlySchema = z.literal(true).optional();

/**
 * `inbound`, `outbound`, or not known — no direction or another value. Lives here,
 * not in `dashboard-flow.ts`, so `records.ts` can take it without an import cycle.
 */
export const dashboardFlowDirectionSchema = z.enum(['inbound', 'outbound', 'unknown']);

// ============================================================================
// Error Schemas
// ============================================================================

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
  code: z.string().optional(),
  details: z.unknown().optional(),
});

export const validationErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  code: z.literal('VALIDATION_ERROR'),
  details: z.array(
    z.object({
      field: z.string(),
      message: z.string(),
    })
  ),
});

// ============================================================================
// Search
// ============================================================================

/**
 * Сколько символов поискового запроса осталось после обрезки.
 *
 * ПРИСУТСТВИЕ ПОЛЯ — И ЕСТЬ ПРИЗНАК ОБРЕЗКИ; отсутствие значит «запрос ушёл
 * в базу целиком». Число даём вместо константы, потому что предел D1 считается
 * в БАЙТАХ шаблона `LIKE`, и в символах он зависит от языка: около 46 для
 * латиницы и около 23 для кириллицы.
 *
 * Интерфейс ОБЯЗАН показать пометку рядом с результатами, когда поле пришло:
 * без неё правка, снявшая громкий пятисотый, превращается в тихий неверный
 * ответ. Обрезанный запрос возвращает надмножество правильного ответа —
 * настоящие совпадения на месте, но добавляются лишние.
 *
 * Опциональное, а не обязательное: обязательное поле в разделяемой схеме
 * убивает разбор ВСЕГО ответа, если сервер его не прислал, — цена
 * рассинхрона деплоев несопоставима с ценой неказанной пометки.
 *
 * У рассинхрона есть и обратная сторона, и она названа честно: новый сервер
 * против старого бандла — Zod молча срежет незнакомый ключ, обрезка
 * произойдёт, а пометка не нарисуется. Это ровно тот тихий неверный ответ,
 * против которого написан абзац выше. Окно короткое (оба воркера едут одним
 * прогоном CI) и всё равно лучше пятисотого, но списывать его в ноль нельзя.
 */
export const searchTruncatedToSchema = z.number().int().nonnegative().optional();

/**
 * `total` у выдачи поиска — НИЖНЯЯ граница, а не полное число (#930).
 *
 * Поиск идёт в два шага: Postgres отдаёт id совпадений порциями, D1 отсеивает
 * из порции всё, чего пользователю не положено видеть или что не проходит его
 * фильтры. Когда страница набралась раньше, чем кончились совпадения, — или
 * когда набор остановил бюджет порций, — сосчитано только просмотренное.
 *
 * Поле-флаг, а не число: величина потолка пользователю ничего не говорит, ему
 * важно одно — что показанное число неполное. Прежнее `searchCappedAt` несло
 * сам потолок и утверждало «совпадений больше 2000»; после #930 потолок больше
 * не отсекает выдачу, и это утверждение стало неверным.
 *
 * Необязательное по той же причине, что `searchTruncatedToSchema`: обязательное
 * поле при рассинхроне деплоев убивает разбор всего ответа.
 */
export const searchTotalIsPartialSchema = z.boolean().optional();

/**
 * Докуда список записей считает `total` (#3193). Полный `count(*)` организации
 * на 154 тыс. записей читает их все и держит очередь D1 ~2,5 с, а D1 исполняет
 * запросы базы по одному: два-три таких подсчёта давали `overloaded` всем
 * арендаторам. Число должно оставаться в пределах нескольких тысяч строк.
 */
export const RECORDS_TOTAL_CAP = 5000;

/**
 * `total` остановлен потолком счёта: записей больше, и показать надо «N+».
 *
 * Отдельно от `searchTotalIsPartialSchema`: тот включает плашку про поиск, а
 * потолок срабатывает и на списке без поиска. Необязательное — по той же
 * причине рассинхрона деплоев.
 */
export const totalIsCappedSchema = z.boolean().optional();

/**
 * Почему выдачу поиска дал запасной D1 LIKE, хотя поиск включён в Postgres
 * (#932). ПРИСУТСТВИЕ ПОЛЯ — признак запасного пути; без него ответ на пути
 * PG и на пути D1 неотличим, а выдачи у движков разные: D1 складывает регистр
 * только у A–Z, и «дарія» на staging дала `pg = 37`, `d1 = 0`. Пользователь,
 * повторивший запрос после икоты Neon, видел 37 → 0 → 37 без единого признака.
 *
 * - `pg_unavailable` — Postgres упал, не ответил за 2,5 с или разомкнут
 *   предохранитель после серии отказов;
 * - `short_query` — запрос короче `RECORD_SEARCH_MIN_PG_CHARS`, в Postgres не
 *   ходили намеренно.
 *
 * Необязательное по той же причине, что `searchTruncatedToSchema`. Вдобавок
 * `.catch(undefined)`: причину, добавленную сервером позже, старый бандл
 * должен пропустить, а не уронить на ней разбор всего ответа.
 */
export const SEARCH_FALLBACK_REASONS = ['pg_unavailable', 'short_query'] as const;
export type SearchFallbackReason = (typeof SEARCH_FALLBACK_REASONS)[number];
export const searchFallbackSchema = z
  .enum(SEARCH_FALLBACK_REASONS)
  .optional()
  .catch(undefined);

/**
 * Потолок каждого поля курсора — защита от мусора, а не предел поиска: он с
 * запасом больше любой организации (на 25.09 крупнейшая — ~150 тыс. записей),
 * иначе сервер однажды получил бы 400 на СВОЙ ЖЕ курсор.
 */
const SEARCH_CURSOR_MAX = 1_000_000_000;

/**
 * Курсор порций поиска (#980): где остановился набор страницы.
 *
 * - `pgOffset` — смещение порции в выдаче Postgres;
 * - `before` — видимых записей насчитано в порциях до неё (для `total`);
 * - `skip` — видимых записей этой порции уже показано.
 *
 * Клиенту курсор непрозрачен: на проводе это строка `base64url(JSON)`
 * (`searchCursorParamSchema`), разбирает её только сервер. Подделка может лишь
 * пропустить записи, не показать чужие: права применяет D1 в том же запросе.
 */
export const searchCursorSchema = z.object({
  pgOffset: z.number().int().min(0).max(SEARCH_CURSOR_MAX),
  before: z.number().int().min(0).max(SEARCH_CURSOR_MAX),
  skip: z.number().int().min(0).max(SEARCH_CURSOR_MAX),
});
export type SearchCursor = z.infer<typeof searchCursorSchema>;

/** Курсор на проводе — в запросе и в ответе. Длина с запасом под три числа. */
export const searchCursorParamSchema = z
  .string()
  .max(200)
  .regex(/^[A-Za-z0-9_-]+$/);

/**
 * В ответе — необязательный и с `.catch(undefined)`: испорченное значение от
 * сервера не должно ронять разбор всего списка, клиент просто продолжит по
 * `offset`, как до курсора.
 */
export const searchCursorResponseSchema = searchCursorParamSchema
  .optional()
  .catch(undefined);

// ============================================================================
// Success Schemas
// ============================================================================

export const successResponseSchema = z.object({
  message: z.string().optional(),
});

export const deleteResponseSchema = z.object({
  message: z.string(),
  deletedId: z.string(),
});

// ============================================================================
// Pagination Schemas
// ============================================================================

export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number().int(),
    limit: z.number().int().optional(),
    offset: z.number().int().optional(),
  });

// ============================================================================
// ID Param Schemas (camelCase, SSOT for all routes)
// ============================================================================

/** Generic ID param (string, no UUID validation) */
export const idParamSchema = z.object({
  id: z.string(),
});

/** Generic UUID param for /:id routes */
export const uuidParamSchema = z.object({
  id: z.string().uuid(),
});

/** Organization ID param for /:orgId routes */
export const orgIdParamSchema = z.object({
  orgId: z.string().uuid(),
});

/** Project ID param for /:projectId routes */
export const projectIdParamSchema = z.object({
  projectId: z.string().uuid(),
});

/** Record ID param for /:recordId routes */
export const recordIdParamSchema = z.object({
  recordId: z.string().uuid(),
});

/** User ID param for /:userId routes (Clerk ID, not UUID) */
export const userIdParamSchema = z.object({
  userId: z.string(),
});

/** Analysis ID param for /:analysisId routes */
export const analysisIdParamSchema = z.object({
  analysisId: z.string().uuid(),
});

/** Script ID param for /:scriptId routes */
export const scriptIdParamSchema = z.object({
  scriptId: z.string().uuid(),
});

/** Profile ID param for /:profileId routes */
export const profileIdParamSchema = z.object({
  profileId: z.string().uuid(),
});

/** Agent ID param for /:agentId routes.
 * agentId can be a Clerk UUID or an external CRM ID (e.g. Bitrix24 numeric ID),
 * so UUID validation is intentionally NOT enforced here. */
export const agentIdParamSchema = z.object({
  agentId: z.string().min(1),
});

// ============================================================================
// Multi-project filter
// ============================================================================

/**
 * Потолок на число проектов в одном фильтре.
 *
 * ЭТО НЕ ВКУСОВОЕ ЧИСЛО. D1 принимает не более 100 связанных параметров на
 * запрос, а фильтр по проектам едет в запрос НЕ ОДИН: рядом с ним лежат
 * границы окна, флаг демо и список агентов роли. Сорок оставляет запас на
 * всё перечисленное и на то, что добавят после нас.
 *
 * Цена пробитого потолка здесь несоразмерна обычному пятисотому: в
 * дашборд-роуте каждый агрегат обёрнут в `.catch()` с нулевым фолбэком,
 * поэтому упавший из-за лимита запрос вернётся НЕ ошибкой, а нулями с
 * пометкой `warnings` — владелец увидит «работает» и примет решение по
 * пустым цифрам. Поэтому предел проверяется здесь и отвечает четырёхсотым,
 * а не деградирует молча ниже по стеку.
 */
export const PROJECT_IDS_MAX = 40;

/**
 * Предел длины текстового запроса в списке записей. Сервер отвечает
 * четырёхсотым на более длинный, поэтому поле ввода держит тот же предел: иначе
 * вставка длинного текста превращала список в ошибку без выхода (#1067).
 */
export const RECORD_SEARCH_MAX_LENGTH = 200;

/**
 * Приводит фильтр проектов к каноничному списку: CSV или повторяющийся
 * параметр → массив без пустых, без пробелов и без повторов.
 *
 * Транспорт — CSV (`?projectIds=a,b`), но массив на входе принимается тоже:
 * `zValidator` отдаёт одно вхождение параметра строкой, а два — массивом
 * (`v.length === 1 ? v[0] : v`), и клиент, собравший ссылку вручную, не
 * должен получать четырёхсотый за форму, которую сервер понимает.
 */
export function normalizeProjectIds(raw: string | string[] | undefined | null): string[] {
  if (raw === undefined || raw === null) return [];

  const parts = Array.isArray(raw) ? raw : [raw];
  const seen = new Set<string>();
  const ids: string[] = [];

  for (const part of parts) {
    for (const piece of part.split(',')) {
      const id = piece.trim();
      if (id.length === 0 || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }

  return ids;
}

/**
 * Фильтр по проектам для query-параметров.
 *
 * ПУСТОЙ СПИСОК ВОЗВРАЩАЕТСЯ КАК `undefined`, а не как `[]`. Пустой выбор
 * означает «все проекты», и единственное место, где это правило живёт, —
 * здесь: иначе `[]` доехал бы до SQL и превратился в `IN ()` в каждом из
 * почти тридцати мест, где строится условие по проекту.
 *
 * Поэлементной проверки на UUID НЕТ намеренно. Идентификаторы проектов не
 * гарантированно UUID на всех поверхностях API — это закреплено отдельными
 * регресс-тестами, и добавление `.uuid()` здесь либо уронило бы их, либо
 * создало асимметрию, где `projectId=abc` проходит, а `projectIds=abc` нет.
 */
export const projectIdsFilterSchema = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform(normalizeProjectIds)
  .refine((ids) => ids.length <= PROJECT_IDS_MAX, {
    message: `Too many projects in filter (max ${PROJECT_IDS_MAX})`,
  })
  .transform((ids) => (ids.length > 0 ? ids : undefined));

// ============================================================================
// Date Range Schemas
// ============================================================================

export const dateRangeQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export const periodQuerySchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
});

// ============================================================================
// Sort Schemas
// ============================================================================

/**
 * Sort order enum - ascending or descending
 * Used in sortable query schemas across all API endpoints
 */
export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');

export const sortableQuerySchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: sortOrderSchema,
});

// ============================================================================
// Stored vendor names — the one answer to "a row written before we retired it"
// ============================================================================

/**
 * A vendor name as it is STORED. For RESPONSE schemas only; requests keep the
 * strict enum.
 *
 * Why this exists at all: retiring a vendor (OpenRouter, 2026-08-11) does not
 * retire the rows that already name it. A response schema pinned to the current
 * enum would fail on such a row and, because a Zod failure discards the WHOLE
 * response, take the entire credential or integration list down with it — over
 * one stale row the customer cannot even see.
 *
 * Why it is generic rather than `z.enum([...current, 'openrouter'])`: that
 * spelling had to be repeated per schema and hand-edited per retirement, and
 * nothing tied the copies together or failed when one was forgotten. The next
 * vendor we retire needs no edit here.
 *
 * The TYPE still names the vendors that are current, so `switch` autocompletion
 * and misspellings are still caught, while `(string & {})` keeps the door open
 * for whatever a historical row actually says. Assign it only to fields whose
 * value comes back FROM storage.
 */
export const storedVendorNameSchema = <Known extends string>() =>
  z.string().min(1) as unknown as z.ZodType<Known | (string & {})>;

// ============================================================================
// Type exports
// ============================================================================

export type ErrorResponse = z.infer<typeof errorResponseSchema>;
export type ValidationError = z.infer<typeof validationErrorSchema>;
export type SuccessResponse = z.infer<typeof successResponseSchema>;
export type DeleteResponse = z.infer<typeof deleteResponseSchema>;
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
export type DateRangeQuery = z.infer<typeof dateRangeQuerySchema>;
export type PeriodQuery = z.infer<typeof periodQuerySchema>;
export type SortOrder = z.infer<typeof sortOrderSchema>;

// ID Param Types
export type IdParam = z.infer<typeof idParamSchema>;
export type UuidParam = z.infer<typeof uuidParamSchema>;
export type OrgIdParam = z.infer<typeof orgIdParamSchema>;
export type ProjectIdParam = z.infer<typeof projectIdParamSchema>;
export type RecordIdParam = z.infer<typeof recordIdParamSchema>;
export type UserIdParam = z.infer<typeof userIdParamSchema>;
export type AnalysisIdParam = z.infer<typeof analysisIdParamSchema>;
export type ScriptIdParam = z.infer<typeof scriptIdParamSchema>;
export type ProfileIdParam = z.infer<typeof profileIdParamSchema>;
export type AgentIdParam = z.infer<typeof agentIdParamSchema>;
